import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  Fade,
  Slide,
  Zoom,
  useTheme,
  alpha,
  Avatar,
  CircularProgress,
  Dialog,
  AppBar,
  Toolbar,
  Container,
  Stack,
  styled,
  FormControl,
  FormControlLabel,
  RadioGroup,
  FormLabel,
  Radio,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Close,
  AutoAwesome,
  Send,
  Campaign,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { FaRobot } from "react-icons/fa";
import useApiEp from "../hooks/useApiEp";
import Draggable from "react-draggable";
import {
  getAiFullScreen,
  getAiMode,
  getAiOpen,
  getHeader,
  setAiFullScreen,
  setAiMode,
  setAiOpen,
  toastError,
} from "../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { LoadingButton } from "@mui/lab";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import { useDialog } from "../context/DialogContext";
import { toast } from "react-toastify";
import { CommonAiOptions } from "../components/CommonAiOptions";
import ChatOptions from "./ChatOptions";
import SelectTemplate from "./SelectTemplate";
import MediaSelector from "./MediaSelector";
import { GlassButtonLoading } from "../themes/UiElements";
const isAgent = (role) => {
  return role === "talkAgent" || role === "system" || role === "assistant";
};
const FullWidthContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    paddingLeft: 0,
    paddingRight: 0,
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: theme.breakpoints.values.lg, // or any other maxWidth
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));
const CampaignCreatorPrompt = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [prompt, setPrompt] = useState("");
  const { hideAiDialog, showAiDialog } = useDialog();
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(getAiOpen() === "open");
  const [type, setType] = useState(getAiMode() ? getAiMode() : "general");
  const [localChats, setLocalChats] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(
    getAiFullScreen() === "open"
  );
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textFieldRef = useRef(null);

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/agent/get-ai-chats?type=${type}`
  );

  // Initialize local chats with data from API
  useEffect(() => {
    if (data) {
      setLocalChats(data);
    }
  }, [data]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [localChats, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleStop = (e, data) => {
    setPosition({ x: data.x, y: data.y });
  };
  const handleOptionSelect = (selectedValue) => {
    console.log("selected Value", selectedValue);
    setPrompt(selectedValue);
    sendMessage(selectedValue);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      sendMessage(prompt);
    },
    [prompt]
  );
  const sendMessage = async (value) => {
    if (!value.trim()) {
      return;
    }
    try {
      setIsSaving(true);
      // Add user message immediately to the UI
      const userMessage = {
        from: "user",
        to: "talkAgent",
        message: value,
        createdOn: new Date().toISOString(),
        _id: `temp-${Date.now()}`,
      };

      setLocalChats((prev) => [...prev, userMessage]);
      setPrompt("");

      // Show typing indicator
      setIsTyping(true);

      // Send to API
      const response = await axios.post(
        type === "general"
          ? `${API_ENDPOINT}/agent/post-ai-chat?type=${type}`
          : `${API_ENDPOINT}/agent/talk-campaign-agent?type=${type}`,
        { message: value },
        getHeader()
      );

      // Remove typing indicator and add the actual response
      setIsTyping(false);

      if (response.data) {
        setLocalChats((prev) => [...prev, response.data]);
      }

      // Refetch to ensure we have the latest data
      refetch();
    } catch (error) {
      setIsTyping(false);
      toastError(error);
    } finally {
      setIsSaving(false);
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setAiOpen("closed");
  };

  const handleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
    setAiFullScreen(isFullScreen ? "closed" : "open");
  };

  const handleSettings = useCallback(() => {
    showAiDialog(
      "AI capabilities!",
      `You are in ${type} mode. Select the mode to perform specialised tasks?`,
      {
        aiOptions: ["general", "database", "carousel", "sendCarousel"],
        type: "info",
        onConfirm: async (mode) => {
          setAiMode(mode);
          setType(mode);
          hideAiDialog();
          toast.success(`Switch to ${mode} successfully!`);
        },
      }
    );
  }, [type]);

  // Handle multiline input with Shift+Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const chatContent = (
    <>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, 
      ${alpha("#1877F2", 0.15)} 0%, 
      ${alpha("#25D366", 0.15)} 50%, 
      ${alpha("#E1306C", 0.15)} 100%)`,
          color: theme.palette.text.primary,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
          backdropFilter: "blur(20px)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, 
        ${alpha("#ffffff", 0.4)} 0%, 
        ${alpha("#ffffff", 0.2)} 50%, 
        ${alpha("#ffffff", 0.1)} 100%)`,
            zIndex: 1,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: `conic-gradient(
        from 0deg at 50% 50%,
        ${alpha("#1877F2", 0.1)} 0deg,
        ${alpha("#25D366", 0.1)} 120deg,
        ${alpha("#E1306C", 0.1)} 240deg,
        ${alpha("#1877F2", 0.1)} 360deg
      )`,
            animation: "rotate 10s linear infinite",
            zIndex: 0,
          },
          "@keyframes rotate": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      >
        <Box
          flex={1}
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <AutoAwesome
            sx={{
              mr: 1,
              color: theme.palette.primary.main,
              filter: "drop-shadow(0 0 8px rgba(24, 119, 242, 0.3))",
            }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              color={theme.palette.text.primary}
              sx={{
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                background: `linear-gradient(135deg, #1877F2, #25D366, #E1306C)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              AI Agent!
            </Typography>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              sx={{
                textShadow: "0 1px 1px rgba(255,255,255,0.8)",
              }}
            >
              {type} mode. use settings to change
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={handleFullScreen}
            size="small"
            sx={{
              color: theme.palette.text.primary,
              mr: 1,
              background: alpha(theme.palette.common.white, 0.3),
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
              "&:hover": {
                background: alpha(theme.palette.common.white, 0.5),
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <IconButton
            onClick={handleSettings}
            size="small"
            sx={{
              color: theme.palette.text.primary,
              mr: 1,
              background: alpha(theme.palette.common.white, 0.3),
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
              "&:hover": {
                background: alpha(theme.palette.common.white, 0.5),
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <SettingsSuggestOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.primary,
              background: alpha(theme.palette.common.white, 0.3),
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
              "&:hover": {
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                transform: "scale(1.05)",
                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.2)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          backgroundColor: alpha("#f9f9f9", 0.95),
          backdropFilter: "blur(5px)",
        }}
      >
        <FullWidthContainer>
          <Stack gap={2}>
            {localChats.length === 0 ? (
              <Slide direction="up" in={isOpen} timeout={800}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#1877F2",
                      width: 32,
                      height: 32,
                      mr: 1,
                    }}
                  >
                    <FaRobot
                      style={{
                        fontSize: 18,
                        color: theme.palette.common.white,
                      }}
                    />
                  </Avatar>
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(
                        "#ffffff",
                        0.8
                      )} 0%, ${alpha("#f0f0f0", 0.9)} 100%)`,
                      borderRadius: 2,
                      p: 2,
                      position: "relative",
                      maxWidth: "85%",
                      border: `1px solid ${alpha("#1877F2", 0.3)}`,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      backdropFilter: "blur(15px)",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        bottom: -8,
                        left: 12,
                        width: 0,
                        height: 0,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderTop: `8px solid ${alpha("#ffffff", 0.8)}`,
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.primary,
                        lineHeight: 1.5,
                      }}
                    >
                      {type === "general"
                        ? `I am an AI agent ready to streamline all your task and
                    optimize your campaign and generate values for you! Tune in!`
                        : type === "database"
                        ? `I am an AI agent ready to do a campaign in our curated audience collection. `
                        : type === "carousel"
                        ? `I am an AI agent ready to create an approve media rich carousel for you.`
                        : `I am an AI agent ready to assist you do rich media campaign with carousel. say hi to me.`}
                    </Typography>
                  </Box>
                </Box>
              </Slide>
            ) : (
              <>
                {localChats.map((chat, index) => (
                  <Box
                    key={chat._id || index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: isAgent(chat.from)
                        ? "flex-start"
                        : "flex-end",
                    }}
                  >
                    {isAgent(chat.from) && (
                      <Avatar
                        sx={{
                          bgcolor: "#1877F2",
                          width: 32,
                          height: 32,
                          mr: 1,
                        }}
                      >
                        <FaRobot
                          style={{
                            fontSize: 18,
                            color: theme.palette.common.white,
                          }}
                        />
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        background: isAgent(chat.from)
                          ? `linear-gradient(135deg, ${alpha(
                              "#ffffff",
                              0.8
                            )} 0%, ${alpha("#f0f0f0", 0.9)} 100%)`
                          : `linear-gradient(135deg, ${alpha(
                              "#E1306C",
                              0.1
                            )} 0%, ${alpha("#25D366", 0.1)} 100%)`,
                        borderRadius: 2,
                        p: 2,
                        position: "relative",
                        maxWidth: "85%",
                        border: `1px solid ${alpha(
                          isAgent(chat.from) ? "#1877F2" : "#E1306C",
                          0.3
                        )}`,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        overflow: "hidden", // Prevent content from overflowing the box
                        backdropFilter: "blur(15px)",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          bottom: -8,
                          left: isAgent(chat.from) ? 12 : "auto",
                          right: isAgent(chat.from) ? "auto" : 12,
                          width: 0,
                          height: 0,
                          borderLeft: "8px solid transparent",
                          borderRight: "8px solid transparent",
                          borderTop: `8px solid ${alpha(
                            isAgent(chat.from) ? "#ffffff" : "#E1306C",
                            0.1
                          )}`,
                        },
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => (
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.primary,
                                lineHeight: 1.5,
                                wordBreak: "normal",
                                overflowWrap: "break-word",
                                whiteSpace: "normal",
                              }}
                              {...props}
                            />
                          ),
                          code: ({ node, ...props }) => (
                            <Box
                              component="code"
                              sx={{
                                wordBreak: "normal",
                                overflowWrap: "break-word",
                                whiteSpace: "normal",
                                backgroundColor: alpha(
                                  theme.palette.grey[200],
                                  0.8
                                ),
                                p: 0.5,
                                borderRadius: 1,
                              }}
                              {...props}
                            />
                          ),
                          pre: ({ node, ...props }) => (
                            <Box
                              component="pre"
                              sx={{
                                overflowX: "auto",
                                maxWidth: "100%",
                                wordBreak: "normal",
                                overflowWrap: "break-word",
                                backgroundColor: alpha(
                                  theme.palette.grey[200],
                                  0.8
                                ),
                                p: 1,
                                borderRadius: 1,
                              }}
                              {...props}
                            />
                          ),
                          a: ({ node, ...props }) => (
                            <Typography
                              component="a"
                              sx={{
                                color: "#1877F2", // Facebook blue for links
                                textDecoration: "underline",
                                "&:hover": {
                                  color: "#25D366", // WhatsApp green on hover
                                  textDecoration: "underline",
                                },
                              }}
                              {...props}
                            />
                          ),
                        }}
                      >
                        {chat.message}
                      </ReactMarkdown>

                      {chat.required === "template" ? (
                        <SelectTemplate
                          disabled={localChats.length - 1 !== index}
                          onSelect={handleOptionSelect}
                        />
                      ) : chat.required === "image" ? (
                        <MediaSelector
                          type="image"
                          selection={chat?.selection ?? "single"}
                          selectionLimit={chat?.selectionLimit ?? 1}
                          disabled={localChats.length - 1 !== index}
                          onSelected={handleOptionSelect}
                        />
                      ) : (
                        <ChatOptions
                          chat={chat}
                          index={index}
                          localChats={localChats}
                          handleOptionSelect={handleOptionSelect}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          color: alpha(theme.palette.text.secondary, 0.7),
                          mt: 1,
                        }}
                      >
                        {new Date(chat.createdOn).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    {!isAgent(chat.from) && (
                      <Avatar
                        sx={{
                          bgcolor: "#25D366",
                          width: 32,
                          height: 32,
                          ml: 1,
                        }}
                      >
                        <AccountCircleOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.common.white,
                          }}
                        />
                      </Avatar>
                    )}
                  </Box>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#1877F2",
                        width: 32,
                        height: 32,
                        mr: 1,
                      }}
                    >
                      <FaRobot
                        style={{
                          fontSize: 18,
                          color: theme.palette.common.white,
                        }}
                      />
                    </Avatar>
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          "#ffffff",
                          0.8
                        )} 0%, ${alpha("#f0f0f0", 0.9)} 100%)`,
                        borderRadius: 2,
                        p: 2,
                        position: "relative",
                        maxWidth: "85%",
                        border: `1px solid ${alpha("#1877F2", 0.3)}`,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        backdropFilter: "blur(15px)",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          bottom: -8,
                          left: 12,
                          width: 0,
                          height: 0,
                          borderLeft: "8px solid transparent",
                          borderRight: "8px solid transparent",
                          borderTop: `8px solid ${alpha("#ffffff", 0.8)}`,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <CircularProgress size={12} sx={{ color: "#E1306C" }} />
                        <CircularProgress size={12} sx={{ color: "#1877F2" }} />
                        <CircularProgress size={12} sx={{ color: "#25D366" }} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </Stack>
        </FullWidthContainer>
      </Box>
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha("#1877F2", 0.2)}`,
          backgroundColor: alpha("#ffffff", 0.95),
          backdropFilter: "blur(10px)",
        }}
      >
        <FullWidthContainer>
          <Zoom in={isOpen} timeout={1000}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Talk with me! (Shift+Enter for new line)"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                disabled={isTyping}
                multiline
                maxRows={4}
                inputRef={textFieldRef}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: alpha("#f0f0f0", 0.8),
                    color: theme.palette.text.primary,
                    "&:hover fieldset": {
                      borderColor: "#1877F2",
                    },
                    "& fieldset": {
                      borderColor: alpha("#1877F2", 0.4),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1877F2",
                    },
                    alignItems: "flex-start",
                    backdropFilter: "blur(5px)",
                  },
                  "& .MuiInputBase-input": {
                    color: theme.palette.text.primary,
                    "&::placeholder": {
                      color: alpha(theme.palette.text.secondary, 0.7),
                      opacity: 1,
                    },
                  },
                }}
              />
              <GlassButtonLoading
                loading={isSaving}
                type="submit"
                variant="contained"
                endIcon={<Send />}
                disabled={isTyping || !prompt.trim()}
              >
                Send
              </GlassButtonLoading>
            </Box>
          </Zoom>
        </FullWidthContainer>
      </Box>
    </>
  );

  if (!isOpen) {
    return (
      <Draggable
        position={position}
        onStop={handleStop}
        defaultPosition={{ x: 0, y: 0 }} // Start at default position
      >
        <Box
          sx={{
            position: "fixed",
            bottom: 10,
            right: 16,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "move", // Indicate the component is draggable
          }}
        >
          {/* {showText && (
            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "2px solid #00CED1",
                borderRadius: "12px",
                padding: "6px 12px",
                marginBottom: "12px",
                position: "relative",
                boxShadow:
                  "0 4px 10px rgba(0, 0, 0, 0.1), inset 0 0 10px rgba(255, 255, 255, 0.2)",
                animation: "fadeIn 1.5s ease-in-out",
                "&:after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "8px solid #00CED1",
                },
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Typography
                sx={{
                  color: "#00CED1",
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  textShadow: "0 0 8px rgba(0, 206, 209, 0.7)",
                }}
              >
                Smart Agent
              </Typography>
            </Box>
          )} */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              onClick={() => {
                setIsOpen(true);
                setAiOpen("open");
              }}
              sx={{
                position: "relative",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "2px solid #00CED1",
                width: 60,
                height: 60,
                "&:hover": {
                  background: "rgba(255, 255, 255, 1)",
                  transform: "scale(1.1) rotate(15deg)",
                },
                transition: "all 0.3s ease-in-out",
                boxShadow:
                  "0 4px 20px rgba(0,0,0,0.2), inset 0 0 10px rgba(255, 255, 255, 0.3)",
                zIndex: 9999,
                animation: "pulse 2s infinite, turn 4s linear infinite",
                "@keyframes pulse": {
                  "0%": {
                    transform: "scale(1)",
                    boxShadow: "0 0 0 0 rgba(255, 255, 255, 0.7)",
                  },
                  "70%": {
                    transform: "scale(1.05)",
                    boxShadow: "0 0 0 10px rgba(255, 255, 255, 0)",
                  },
                  "100%": {
                    transform: "scale(1)",
                    boxShadow: "0 0 0 0 rgba(255, 255, 255, 0)",
                  },
                },
                "@keyframes turn": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "100%": {
                    transform: "rotate(360deg)",
                  },
                },
              }}
            >
              <FaRobot />
            </IconButton>
            {/* <IconButton
              size="small"
              onClick={() => setShowText(!showText)}
              sx={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "2px solid #00CED1",
                width: 30,
                height: 30,
                "&:hover": {
                  background: "rgba(255, 255, 255, 1)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease-in-out",
                boxShadow:
                  "0 2px 10px rgba(0,0,0,0.2), inset 0 0 5px rgba(255, 255, 255, 0.3)",
              }}
            >
              {showText ? (
                <VisibilityOff fontSize="small" sx={{ color: "#00CED1" }} />
              ) : (
                <Visibility fontSize="small" sx={{ color: "#00CED1" }} />
              )}
            </IconButton> */}
          </Box>
        </Box>
      </Draggable>
    );
  }
  if (isFullScreen) {
    return (
      <Dialog
        fullScreen
        open={isFullScreen}
        onClose={handleFullScreen}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(
              "#f9f9f9",
              0.95
            )} 0%, ${alpha("#ffffff", 0.98)} 100%)`,
            color: theme.palette.text.primary,
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 16px)",
          }}
        >
          {chatContent}
        </Box>
      </Dialog>
    );
  }

  return (
    <Fade in={isOpen} timeout={500}>
      <Paper
        elevation={24}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 380,
          maxWidth: "90vw",
          height: 500,
          maxHeight: "70vh",
          borderRadius: 2,
          overflow: "hidden",
          color: theme.palette.text.primary,
          display: "flex",
          flexDirection: "column",
          background: `linear-gradient(135deg, ${alpha(
            "#f9f9f9",
            0.95
          )} 0%, ${alpha("#ffffff", 0.98)} 100%)`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha("#1877F2", 0.4)}`,
          zIndex: 10000,
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
      >
        {chatContent}
      </Paper>
    </Fade>
  );
};

export default CampaignCreatorPrompt;
